import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faRobot } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { SPORT_LABEL, SPORT_VERB } from '../utils/sportData';
import { formatDuration } from '../utils/dateUtils';
import '../styles/OpponentProfileCard.css';

interface SportBreakdown {
  sport: string;
  count: number;
  avg_per_week: number;
  avg_minutes: number;
  avg_points: number;
}

interface BestWorkout {
  activity_name: string;
  sport_type: string;
  duration_seconds: number;
  total_points: number;
  zone_breakdown: Record<string, number> | null;
}

interface OpponentProfile {
  display_name: string;
  avatar_url: string | null;
  level_value: number;
  is_paused?: boolean;
  last_week_pts: number;
  last_week_workouts: number;
  avg_weekly_pts: number;
  avg_weekly_workouts: number;
  total_weeks: number;
  wins: number;
  losses: number;
  has_consolation: boolean;
  sports: SportBreakdown[];
  best_workout: BestWorkout | null;
  global_rank: number;
  total_players: number;
  elo_rating: number;
  best_onfire: number;
  worst_onfire: number;
}

interface Props {
  opponentId: number;
  opponentName: string;
  onDismiss: () => void;
}

const getFullLabel = (sport: string) => SPORT_LABEL[sport] ?? sport;
const getVerb = (sport: string) => SPORT_VERB[sport] ?? sport.toLowerCase() + 's';

function getZoneLabel(zone: number): string {
  const labels: Record<number, string> = {
    1: 'Recovery', 2: 'Endurance', 3: 'Tempo', 4: 'Threshold', 5: 'VO2max'
  };
  return labels[zone] ?? `Z${zone}`;
}

/**
 * Builds a profile in natural prose — all info inline, no separate sections.
 * Roughly 10-12 sentences for a full picture.
 */
function buildProfileText(profile: OpponentProfile): string {
  const parts: string[] = [];
  const name = profile.display_name;

  // Main sport + habit
  if (profile.sports.length > 0) {
    const main = profile.sports[0];
    const mainLabel = getFullLabel(main.sport);
    parts.push(
      `${name} is a dedicated ${mainLabel.toLowerCase()} enthusiast, training about ${main.avg_per_week} times a week for roughly ${main.avg_minutes} minutes per session.`
    );
    // Sport breakdown with counts
    const sportCounts = profile.sports.map(s => `${s.count}× ${getFullLabel(s.sport).toLowerCase()}`).join(', ');
    parts.push(`Over the last cycle, that breaks down to ${sportCounts}.`);
  } else {
    parts.push(`${name} has been active over the past ${profile.total_weeks} weeks.`);
  }

  // Side sports (skip if only 1 sport)
  if (profile.sports.length === 2) {
    const side = getFullLabel(profile.sports[1].sport).toLowerCase();
    parts.push(`When not focusing on the main discipline, ${name} also mixes in ${side} for variety.`);
  } else if (profile.sports.length > 2) {
    const sides = profile.sports.slice(1).map(s => getFullLabel(s.sport).toLowerCase());
    parts.push(`Beyond the core sport, ${name} keeps things interesting with ${sides.join(' and ')} on the side.`);
  }

  // Overall consistency + points
  parts.push(
    `In terms of volume, ${name} averages ${profile.avg_weekly_workouts} workouts per week, pulling in around ${profile.avg_weekly_pts} points on a typical week.`
  );
  parts.push(
    `Last week was a strong one — ${profile.last_week_pts} points across ${profile.last_week_workouts} sessions.`
  );

  // Win/loss record
  if (profile.wins + profile.losses > 0) {
    const totalDuels = profile.wins + profile.losses;
    if (profile.losses === 0) {
      parts.push(
        `${name} hasn't lost a single duel yet — ${profile.wins} wins from ${totalDuels} matchups with a clean undefeated streak.`
      );
    } else {
      const wlRatio = profile.wins / profile.losses;
      const descriptor = wlRatio >= 2 ? 'impressive' : wlRatio >= 1 ? 'solid' : 'tough';
      parts.push(
        `Across ${totalDuels} duels so far, the record is ${profile.wins} wins and ${profile.losses} losses — a ${descriptor} run.`
      );
    }
  }

  // Consolation
  if (profile.has_consolation) {
    parts.push(`Worth noting: ${name} is currently carrying consolation points from a previous loss, so the total score already has a buffer built in.`);
  }

  // Best workout
  if (profile.best_workout) {
    const bw = profile.best_workout;
    const sportLabel = getFullLabel(bw.sport_type);
    const dur = formatDuration(bw.duration_seconds);
    let zoneText = '';
    if (bw.zone_breakdown) {
      const zones = Object.entries(bw.zone_breakdown)
        .filter(([, pct]) => (pct as number) > 5)
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .slice(0, 2)
        .map(([z]) => getZoneLabel(parseInt(z)));
      if (zones.length > 0) zoneText = `, spending most of the time in ${zones.join(' and ')} zones,`;
    }
    parts.push(
      `The standout session was a ${dur} ${sportLabel.toLowerCase()} — ${zoneText ? zoneText + ' ' : ''}which earned ${Math.round(bw.total_points)} points on its own.`
    );
  }

  // Closing: OnFire + rank context
  if (profile.elo_rating && profile.global_rank && profile.total_players) {
    const rank = profile.global_rank;
    const total = profile.total_players;
    const elo = profile.elo_rating;
    const best = profile.best_onfire;
    const worst = profile.worst_onfire;
    const range = best - worst;
    const pos = range > 0 ? Math.round(((elo - worst) / range) * 100) : 50;
    const closings = [
      `Ranked ${rank} out of ${total} with an OnFire rating of ${elo} — ${pos >= 80 ? 'near the top of the leaderboard' : pos >= 50 ? 'solidly in the upper half' : pos >= 25 ? 'in the middle of the pack' : 'climbing from the lower ranks'}.`,
      `Currently ${rank}th out of ${total} in the standings, OnFire at ${elo} — ${pos >= 75 ? 'one of the hottest players right now' : pos >= 50 ? 'warming up nicely' : pos >= 25 ? 'steady and building momentum' : 'looking to heat things up'}.`,
      `With an OnFire score of ${elo} and rank ${rank} of ${total}, ${name} is ${pos >= 75 ? 'burning through the competition' : pos >= 50 ? 'holding strong in the upper half' : pos >= 25 ? 'keeping pace with the field' : 'an underdog with potential'}.`,
      `Positioned at ${rank} of ${total} overall, OnFire rating sits at ${elo} — ${pos >= 80 ? 'among the elite' : pos >= 50 ? 'comfortably above average' : pos >= 25 ? 'right in the thick of it' : 'hungry for a breakout week'}.`,
    ];
    const idx = rank % closings.length;
    parts.push(closings[idx]);
  } else {
    const fallbacks = [
      `All in all, ${name} brings consistent effort every week — not someone you can count on to fade.`,
      `${name} shows up regularly and puts in the work — expect a fight.`,
      `Bottom line: ${name} is in this to compete, not just participate.`,
    ];
    const idx = (profile.wins + profile.losses) % fallbacks.length;
    parts.push(fallbacks[idx]);
  }

  return parts.join(' ');
}

export default function OpponentProfileCard({ opponentId, opponentName, onDismiss }: Props) {
  const { token } = useAuth();
  const [profile, setProfile] = useState<OpponentProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !opponentId) return;
    api.get(`/player/${opponentId}/profile`, token).then((data) => {
      setProfile(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token, opponentId]);

  if (loading) {
    return (
      <div className="opponent-profile-card opponent-profile-card--loading">
        <div className="opponent-profile-loading">Building dossier on {opponentName}…</div>
      </div>
    );
  }

  if (!profile) return null;

  const text = buildProfileText(profile);

  return (
    <div className="opponent-profile-card">
      <div className="opponent-profile-header">
        <div className="opponent-profile-avatar-wrap">
          {profile.avatar_url && !profile.avatar_url.includes('robot')
            ? <img src={profile.avatar_url} alt={profile.display_name} className="opponent-profile-avatar" title={profile.display_name} />
            : <FontAwesomeIcon icon={faRobot} className="opponent-profile-avatar-icon" />
          }
        </div>
        <div className="opponent-profile-title">
          <span className="opponent-profile-label">New Opponent</span>
          <span className="opponent-profile-name">
            {profile.display_name}
            {profile.is_paused && <span className="player-paused-badge" title="pausiert – keine Strava-Verbindung">⏸</span>}
          </span>
        </div>
        <button className="opponent-profile-close" onClick={onDismiss}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <p className="opponent-profile-text">{text}</p>
    </div>
  );
}
